import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import deleteSavedAttachment from 'core/application/employeeDashboard/getReimbursed/reimbursementRequest/selectFromLibrary/deleteAttachment'
import getAttachments from 'core/application/employeeDashboard/getReimbursed/reimbursementRequest/selectFromLibrary/savedAttachments'
import { Attachment } from 'core/domain/expenses/attachment'

import { Button, Col, Icon, Row } from 'common/components'
import { useCommandRequestV2, useQueryRequestV2 } from 'common/hooks'
import { UploadFileProps, handleDuplicateFileNaming } from 'common/uploadFile'

import AttachmentPreview from '../../../attachmentPreview/AttachmentPreview'
import FileUpload from '../../../fileUpload/FileUpload'
import SelectFromExistingModal from '../selectFromLibrary'

type ExpenseAttachmentUploaderProps = {
  initialValues: Attachment[]
  onUploadedFilesChanged?: (filesForUpload: UploadFileProps[]) => void
  onSelectedAttachmentsChanged?: (attachments: Attachment[]) => void
}

const ExpenseAttachmentUploader = (props: ExpenseAttachmentUploaderProps) => {
  const { t: translate } = useTranslation(['ee-dashboard', 'translation'])

  const [selectFromLibrary, setSelectFromLibrary] = useState<boolean>(false)
  const [filesToBeUploaded, setFilesToBeUploaded] = useState<UploadFileProps[]>([])
  const {
    data: attachments,
    isLoading: isAttachmentsLoading,
    sendQuery: reloadAttachments,
  } = useQueryRequestV2<Attachment[]>({
    initialState: [],
    queryFn: getAttachments,
  })
  const { sendCommand: sendDeleteAttachment } = useCommandRequestV2((attachmentId: string) =>
    deleteSavedAttachment(attachmentId),
  )
  const [selectedAttachments, setSelectedAttachments] = useState<Attachment[]>(props.initialValues)

  const setSelectedAttachmentsFromGallery = (attachments: Attachment[]) => {
    setSelectedAttachments(attachments)
    if (props.onSelectedAttachmentsChanged) {
      props.onSelectedAttachmentsChanged(attachments)
    }
    setSelectFromLibrary(false)
  }

  useEffect(() => {
    if (selectFromLibrary) {
      ;(async () => {
        await reloadAttachments()
      })()
    }
  }, [selectFromLibrary])

  const deleteAttachment = async (attachmentId: string) => {
    const result = await sendDeleteAttachment(attachmentId)
    if (result.completed) {
      await reloadAttachments()
    }
  }

  const handleFileChange = useCallback(
    (file: File) => {
      const newList = handleDuplicateFileNaming(file, filesToBeUploaded)

      if (props.onUploadedFilesChanged) {
        props.onUploadedFilesChanged(newList)
      }

      setFilesToBeUploaded(newList)
    },
    [filesToBeUploaded, props.onUploadedFilesChanged],
  )

  return (
    <div>
      <Row gutter={[24, 12]}>
        <Col sm={24} md={12}>
          <FileUpload accept={['.png', '.jpg', '.pdf', '.jpeg']} fileChoosed={handleFileChange} translate={translate} />
        </Col>

        <Col sm={24} md={12}>
          <Button type="default" htmlType="button" className="full-width" onClick={() => setSelectFromLibrary(true)}>
            <div className="flex items-center justify_center">
              <Icon className="fa fa-file-image mt-4 justify_center pr-8" />
              <span className="truncate-text">{translate('get-reimburse.request.select-from-gallery-label')}</span>
            </div>
          </Button>

          <SelectFromExistingModal
            visible={selectFromLibrary}
            attachments={attachments}
            loadingAttachments={isAttachmentsLoading}
            deleteAttachment={deleteAttachment}
            onCancel={() => {
              setSelectFromLibrary(false)
            }}
            setSelectedAttachments={setSelectedAttachmentsFromGallery}
          />
        </Col>
      </Row>

      {filesToBeUploaded.length !== 0 && (
        <div className="mt-24">
          {filesToBeUploaded.map((item: UploadFileProps, index: number) => (
            <AttachmentPreview
              key={index}
              uploadedFile={item}
              deletable
              onDelete={() => {
                const newFileList = [...filesToBeUploaded].filter((file) => file.id !== item.id)
                setFilesToBeUploaded(newFileList)
                if (props.onUploadedFilesChanged) {
                  props.onUploadedFilesChanged(newFileList)
                }
              }}
              showSaveForLater
              onSaveForLaterChange={(checked: boolean) => {
                const newList = [...filesToBeUploaded]
                const file = newList.find((i) => i.file === item.file)
                if (file) {
                  file.saveForLater = checked
                }
                setFilesToBeUploaded(newList)
              }}
              className="mb-8"
            />
          ))}
        </div>
      )}

      {selectedAttachments.map((item: Attachment, index: number) => (
        <AttachmentPreview
          key={index}
          attachment={item}
          deletable
          onDelete={() => {
            const newFileList = [...selectedAttachments].filter((file) => file.attachmentId !== item.attachmentId)
            if (props.onSelectedAttachmentsChanged) {
              props.onSelectedAttachmentsChanged(newFileList)
            }
            setSelectedAttachments(newFileList)
          }}
          className="mb-8"
        />
      ))}
    </div>
  )
}

export default ExpenseAttachmentUploader
